import { displayMoney } from '@aims/shared/shared/utils';
import { ExportOutlined } from '@ant-design/icons';
import { Button, message, notification } from 'antd';
import { ExportToCsv } from 'export-to-csv';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import {
  allHpInstallmentsQuery,
  extractAllHpInstallments,
} from './ViewHolisticProject/tabs/PaymentPlanTab/constants';
import getHpInstallmentStatus from './ViewHolisticProject/tabs/PaymentPlanTab/getHpInstallmentStatus';

function getInstallmentStatus(t, nowTs = Date.now()) {
  const amount = Number(t.amount || 0);
  const paid = Number(t.paidAmount || 0);
  const dueTs = t.dueAt ? new Date(t.dueAt).getTime() : undefined;

  if (amount <= 0) return 'N/A';
  if (paid >= amount) return 'Paid';
  if (dueTs && dueTs < nowTs) return paid > 0 ? 'Overdue (Partial)' : 'Overdue';
  if (paid > 0) return 'Partial';
  return 'Scheduled';
}

function doExport(rows, locale, title) {
  const data = rows.map((t) => {
    console.log("Export object!");
    console.log(t);
    const statusObj = getHpInstallmentStatus(t);
    return {
      projectNumber: t.projectNo,
      projectName: t.projectName,
      installmentId: t._id,
      status: statusObj?.label || '',
      amount: displayMoney((t.amount || 0) / 10000),
      dueOn: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
        new Date(t.dueAt),
      ),
      paidAmount: displayMoney((t.paidAmount || 0) / 10000),
      paidOn: t.paidAt
        ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
            new Date(t.paidAt),
          )
        : 'N/A',
      regionCode: t.regionCode,
      notes: t.notes,
    };
  });
  const options = {
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalSeparator: '.',
    showLabels: true,
    showTitle: true,
    title,
    useTextFile: false,
    useBom: true,
    filename: title,
    headers: [
  'Project Number',
      'Project Name',
      'Installment ID',
  'Status',
      'Amount',
      'Due On',
      'Paid Amount',
      'Paid On',
      'Region Code',
      'Notes',
    ],
  };

  const csvExporter = new ExportToCsv(options);

  csvExporter.generateCsv(data);
}

async function fetchInstallmentsForProject(apollo, projectId) {
  let after = undefined;
  const all = [];
  // Fetch pages until done to be safe if a project has many installments
  // Use a generous page size to minimize round trips
  // Adjust if backend enforces a max
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data } = await apollo.query({
      query: allHpInstallmentsQuery,
      variables: {
        first: 1000,
        after,
        filters: { projectId },
      },
      fetchPolicy: 'network-only',
    });
    const conn = extractAllHpInstallments(data);
    if (conn?.edges?.length) {
      for (const { node } of conn.edges) all.push(node);
    }
    const pageInfo = conn?.pageInfo;
    if (!pageInfo?.hasNextPage) break;
    after = pageInfo.endCursor;
  }
  return all;
}

function ExportAllPaymentPlansBtn({ projects }) {
  const [exporting, setExporting] = useState(false);
  const locale = useSelector((store) => store.locale, shallowEqual);
  const apollo = useApolloClient();

  const handleExportCsv = useCallback(async () => {
    // console.debug('ExportAllPaymentPlansBtn clicked');

    const hide = message.loading({ content: 'Preparing export…', duration: 0 });
    setExporting(true);
    try {
      if (!projects || projects.length === 0) {
        notification.info({ message: 'No projects to export.' });
        return;
      }

      // Fetch installments for each visible project, then flatten and enrich
      const results = await Promise.all(
        projects.map(async (p) => {
          const items = await fetchInstallmentsForProject(apollo, p._id);
          return items.map((it) => ({
            ...it,
            projectNo: p.projectNo,
            projectName: p.projectName,
          }));
        }),
      );
      const rows = results.flat();

      if (!rows.length) {
        notification.info({ message: 'No repayment plans found.' });
        return;
      }

      const title = `All Repayment Plans - ${new Date()
        .toISOString()
        .slice(0, 10)}`;
      doExport(rows, locale, title);
      message.success('Export complete');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      notification.error({
        message: 'Error',
        description: 'We apologize. An error occurred during export.',
      });
    } finally {
      hide();
      setExporting(false);
    }
  }, [apollo, locale, projects]);

  return (
    <Button icon={<ExportOutlined />} onClick={handleExportCsv} loading={exporting}>
      Export All - CSV
    </Button>
  );
}

ExportAllPaymentPlansBtn.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      projectName: PropTypes.string,
    }),
  ),
};

ExportAllPaymentPlansBtn.defaultProps = {
  projects: [],
};

export default ExportAllPaymentPlansBtn;
